import React, { useCallback, useState } from "react";
import { X } from "lucide-react";
import RegisterInterestDialog from "@/components/RegisterInterestDialog";
import {
  dismissInterestBanner,
  isInterestBannerDismissed,
} from "@/lib/interest";

/**
 * Thin closed-beta banner + shared register-interest dialog.
 * Use `openDialog` from the render prop / footer link to reopen after dismiss.
 */
export function useClosedBetaInterest() {
  const [showBanner, setShowBanner] = useState(
    () => !isInterestBannerDismissed()
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const dismiss = useCallback(() => {
    dismissInterestBanner();
    setShowBanner(false);
  }, []);

  const openDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  return {
    showBanner,
    dismiss,
    dialogOpen,
    setDialogOpen,
    openDialog,
  };
}

export function ClosedBetaInterestBanner({ onRegister, onDismiss }) {
  return (
    <div
      className="relative z-30 border-b border-primary/25 bg-primary/10 text-foreground"
      role="region"
      aria-label="Closed beta"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2 sm:px-6">
        <p className="min-w-0 flex-1 text-center text-xs leading-snug sm:text-sm">
          Circa is in closed beta. Have an invite? Sign up. Otherwise,{" "}
          <button
            type="button"
            className="font-medium text-primary underline-offset-4 hover:underline"
            onClick={onRegister}
          >
            register interest
          </button>
          .
        </p>
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-background/60 hover:text-foreground"
          aria-label="Dismiss banner"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Banner (if not dismissed) + dialog. Pass `openDialog` to footer links.
 */
export default function ClosedBetaInterestChrome({ children }) {
  const interest = useClosedBetaInterest();

  return (
    <>
      {interest.showBanner ? (
        <ClosedBetaInterestBanner
          onRegister={interest.openDialog}
          onDismiss={interest.dismiss}
        />
      ) : null}
      {typeof children === "function" ? children(interest) : children}
      <RegisterInterestDialog
        open={interest.dialogOpen}
        onOpenChange={interest.setDialogOpen}
      />
    </>
  );
}
